import React from "react";
import { confirmarAntesDeCambiarDeMarca } from "../hooks/useBrandProfile";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCardAyudaSidebar } from "./CardAyudaSidebar";
import {
  Loader2,
  ChevronsLeft,
  ChevronsRight,
  Menu,
  X,
  ChevronLeft,
  AlertCircle,
  ChevronsUpDown,
  Check,
  Plus,
  Lock,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { usePermissions } from "../context/PermissionContext";
import {
  urlsWhatsApp,
  MENSAJE_SOPORTE,
} from "./ContactarPorWhatsApp";
import { Icon } from "../design-system/icons";
import { isPaidPlan, isProOrUltra } from "../lib/planCapabilities";
import { MOBILE_BOTTOM_NAV_PATHS } from "./mobileNavConfig";
import { CampanaNotificaciones } from './notificaciones/CampanaNotificaciones';
import { useSidebar } from "../context/SidebarContext";
import { supabase } from "../lib/supabase";
import { cerrarSesion } from "../lib/cerrarSesion";
import { retrySupabase } from "../utils/retry";
import { decidirMarcaNueva, MOTIVO_SOLO_ADMIN } from "../lib/onboardingMarca/marcaNueva";
import { useMarcaNuevaSoloAdmin } from "../hooks/useMarcaNuevaSoloAdmin";
import { leerLugarParaMarcas, textoDeMarcasUsadas, useLugarParaMarcas } from "../lib/lugarParaMarcas";
import { nombreDelPlanDeEquipo } from "../lib/catalogoDePlanesDeEquipo";
import { identifyEmpresa } from "../lib/posthog";
import SubscriptionLimitModal from "./SubscriptionLimitModal";
import { palabraDeModeloActual } from '../lib/nombreDeModelo';
import { LogoDeMarca, logoDeBranding } from '../branding';
import { estadoDeCobro, type EstadoDeCobro } from "../lib/estadoDeCobro";
import { conCobroDeMarcas, leerRechazoDeCobro } from "../lib/cobroRechazado";
import { conSimulacion } from "../lib/simulacionDeCobro";
import { AvisoDeCobro } from "./cobros/AvisoDeCobro";
import { leerSaldo } from "../lib/saldo";

// Sidebar aplanada: solo pestañas vigentes.
// Accesos en desuso comentados y se indica la categoría original.
// Categorías en desuso (antigua versión):
//  - Ventas (Registro de ventas, Historial de ventas, Mensajes, Clientes, Gráficos de Interacción, Envíos)
//  - Inventario (Lista de productos, Stock, Descuentos)
//  - Compras (Registro de compras, Historial de compras, Forecasting, Proveedores)
//  - Finanzas (Flujo de caja, Forecasting financiero, Gastos recurrentes)

type FlatMenuItem = {
  name: string;
  icon: React.ReactNode;
  path: string;
  permissionKey: string; // clave en PermissionContext
  // categoría original (solo para referencia, no visible)
  category: "Generación de imágenes" | "Empleado IA" | "Estudio de marketing" | "Marca";
  devOnly?: boolean;
  /** Si true, se dibuja el rótulo de la categoría ARRIBA de este ítem.
   *  Hoy solo lo usa el Estudio de marketing; el resto del menú sigue plano. */
  abreCategoria?: boolean;
  /** Rutas extra que el ítem tiene que sentir como propias para quedar
   *  resaltado. Para ítems que son la puerta de una pantalla con pestañas,
   *  donde cada pestaña es una URL distinta: sin esto, `NavLink` compara
   *  contra `path` a secas y el menú se apaga al cambiar de pestaña.
   *  `path` NO hace falta repetirlo acá. */
  rutasActivas?: string[];
  // Si true, se muestra siempre (sin chequear PermissionContext). Para ítems
  // que no son features gateadas (ej. "Mi marca").
  alwaysShow?: boolean;
  // Ítem del estudio (Interfaz V2 + Modos): se ve siempre, sin permiso ni devOnly.
  // Había también "classic" (el flujo de seleccionar productos, sólo Grupo A + dev),
  // borrado en ADR-2026-10-28-se-borra-el-flujo-clasico. Los ítems sin `studioFlow`
  // mantienen el gating normal (permiso + devOnly).
  studioFlow?: "v2";
  /** Card flotante de ayuda al hover sostenido (solo desktop). Los ítems
   *  auto-explicativos (el inicio de V2, Ayuda, Configuración, perfil) van
   *  sin descripción y no muestran card. */
  descripcion?: string;
};

// Pestañas vigentes (sin separación visible). Comentado en qué categoría estaban.
const FLAT_MENU_ITEMS: FlatMenuItem[] = [
  // Generación de imágenes
  {
    name: "Test V1",
    icon: <span className="material-symbols-rounded">smart_toy</span>,
    path: "/test-v1",
    permissionKey: "test-v1",
    category: "Generación de imágenes",
    devOnly: true,
    descripcion: "Herramienta interna para probar el flujo de generación V1.",
  },
  {
    // Interfaz V2: el estudio (greeting + prompt bar + colecciones de presets).
    // Etiquetado "Estudio virtual" salvo para dev testers, que la ven como
    // "Interfaz V2" (ver resolución de label más abajo).
    name: "Interfaz V2",
    icon: <span className="material-symbols-rounded">auto_awesome</span>,
    path: "/interfaz-v2",
    permissionKey: "interfaz-v2",
    category: "Generación de imágenes",
    studioFlow: "v2",
  },
  {
    // "Modos": lista de modos de generación de V2 (la misma pantalla que el tab
    // de mobile), idéntico a su tab en MobileBottomNav. Va justo después de
    // Interfaz V2, respetando el orden de mobile (Inicio → Modos → Historial).
    name: "Modos",
    icon: <span className="material-symbols-rounded">view_agenda</span>,
    path: "/modos",
    permissionKey: "modos",
    category: "Generación de imágenes",
    studioFlow: "v2",
    descripcion:
      `Todos los modos de generación: con ${palabraDeModeloActual().plural}, escenarios, packs de fotos y más.`,
  },
  {
    name: "Historial",
    icon: <span className="material-symbols-rounded">schedule</span>,
    path: "/redes-sociales/imagenes",
    permissionKey: "imagenes",
    category: "Generación de imágenes",
    descripcion:
      "Todas las imágenes y videos que generaste, listos para ver y descargar.",
  },
  {
    name: "Estudio de Video",
    icon: <span className="material-symbols-rounded">movie_edit</span>,
    path: "/editor-video",
    permissionKey: "editor-video",
    category: "Generación de imágenes",
    devOnly: true,
    descripcion:
      "Creá videos promocionales de tus productos con IA, en una o varias escenas.",
  },
  // Estudio de marketing — Bloque A. `devOnly` mientras esté en pruebas.
  // OJO: sus permissionKey van con default `true` en PermissionContext; el
  // filtro de abajo corta por permiso ANTES de mirar devOnly/isDevTester.
  {
    name: "Mapa",
    icon: <span className="material-symbols-rounded">hub</span>,
    path: "/estudio-marketing/mapa",
    permissionKey: "em-mapa",
    category: "Estudio de marketing",
    devOnly: true,
    abreCategoria: true,
    descripcion:
      "La vista general de tu marketing: fuentes, análisis y acciones conectados en un mapa.",
  },
  {
    name: "Plan",
    icon: <span className="material-symbols-rounded">checklist</span>,
    path: "/estudio-marketing/plan",
    permissionKey: "em-plan",
    category: "Estudio de marketing",
    devOnly: true,
    descripcion:
      "Tu plan de marketing: tareas y acciones concretas sugeridas a partir de tus datos.",
  },
  // Insights y Estrategia se fusionaron en una pantalla con dos pestañas, pero
  // cada pestaña sigue siendo su URL de siempre: el ítem apunta a la primera y
  // `rutasActivas` le suma la segunda para que el menú no se apague al cambiar
  // de pestaña (ADR-2026-08-17-analisis-datos-tabs-por-ruta).
  // Se quedó con `em-insights` — la clave del destino al que apunta — y
  // `em-estrategia` se borró de PermissionContext por quedar sin lector.
  {
    name: "Análisis de datos",
    icon: <span className="material-symbols-rounded">insights</span>,
    path: "/estudio-marketing/insights",
    permissionKey: "em-insights",
    category: "Estudio de marketing",
    devOnly: true,
    rutasActivas: ["/estudio-marketing/estrategia"],
    descripcion:
      "Insights y estrategia generados a partir de los datos de tus canales.",
  },
  {
    name: "Marca",
    icon: <span className="material-symbols-rounded">palette</span>,
    path: "/estudio-marketing/marca",
    permissionKey: "em-marca",
    category: "Estudio de marketing",
    devOnly: true,
    descripcion:
      "La identidad de tu marca: estilo, voz y lineamientos que guían todo el estudio.",
  },
  // Va última porque es la pantalla de configuración del resto: de acá salen
  // los datos que alimentan a las otras cuatro. También se llega tocando
  // cualquiera de los tres nodos de fuente del mapa.
  {
    // "Datos" por pedido de Matías, igual que el título de la pantalla.
    // NO REVERTIR a "Fuentes de datos": así se llama la SECCIÓN de adentro,
    // no la pantalla.
    name: "Datos",
    icon: <span className="material-symbols-rounded">database</span>,
    path: "/estudio-marketing/datos",
    permissionKey: "em-datos",
    category: "Estudio de marketing",
    devOnly: true,
    descripcion:
      "Conectá y administrá las fuentes de datos que alimentan el estudio de marketing.",
  },
  // ANDAMIAJE, no la puerta definitiva. La Puesta en marcha es el flujo de
  // configuración inicial: cuando esto se active, el disparador real va a ser
  // el PRIMER INGRESO del usuario (se lo lleva al flujo solo), no un ítem de
  // menú que haya que ir a buscar. Este ítem existe para poder entrar a
  // probarla mientras tanto, y se saca el día que exista el disparador.
  // MISMA TRAMPA que el resto del bloque: `em-onboarding` va con default `true`
  // en PermissionContext porque el filtro corta por permiso ANTES de mirar
  // `devOnly`/`isDevTester`; con `false` no aparecería ni para los dev testers.
  {
    name: "Puesta en marcha",
    icon: <span className="material-symbols-rounded">rocket_launch</span>,
    path: "/estudio-marketing/onboarding",
    permissionKey: "em-onboarding",
    category: "Estudio de marketing",
    devOnly: true,
    descripcion:
      "El flujo de configuración inicial del estudio de marketing, paso a paso.",
  },
  // Empleado IA
  // El Chat abre la categoría: es el agente con el que se habla directo, así
  // que va primero y arrastra el rótulo "Empleado IA" (hasta ahora la categoría
  // no se dibujaba porque tenía un solo ítem).
  // MISMA TRAMPA que el Estudio de marketing: `chat-atlas` va con default
  // `true` en PermissionContext porque el filtro de abajo corta por permiso
  // ANTES de mirar `devOnly`/`isDevTester`. Con `false` el ítem no aparecería
  // ni para los dev testers.
  // Se llama "Asistente", como en Crear: es la misma conversación
  // (ADR-2026-10-16-el-agente-se-ve-como-el-asistente).
  {
    name: "Asistente",
    icon: <span className="material-symbols-rounded">forum</span>,
    path: "/agente",
    permissionKey: "chat-atlas",
    category: "Empleado IA",
    devOnly: true,
    abreCategoria: true,
    descripcion:
      "Tu asistente de IA: pedile ideas, análisis, imágenes, videos y acciones sobre tu marca.",
  },
  {
    name: "Manager de Redes IA",
    icon: <span className="material-symbols-rounded">smart_toy</span>,
    path: "/redes-sociales/calendario",
    permissionKey: "calendario",
    category: "Empleado IA",
    devOnly: true,
    descripcion:
      "Calendario de publicaciones para tus redes, planificado y gestionado por IA.",
  },
  // Marca — visible para todos (no es una feature gateada).
  {
    name: "Mi marca",
    icon: <span className="material-symbols-rounded">shopping_bag</span>,
    path: "/mi-marca",
    permissionKey: "mi-marca",
    category: "Marca",
    alwaysShow: true,
    descripcion:
      "Tus productos y la información de tu marca, la base de todo lo que generás.",
  },
];

const PLAN_LABELS: Record<string, string> = {
  gratis: "Gratis",
  emprendimiento: "Básico",
  negocio: "Pro",
  enterprise: "Ultra",
  enterprise1: "Ultra",
  enterprise2: "Ultra",
  enterprise3: "Ultra",
  // Custom plan asignado a mano para clientes empresariales — comparte
  // todas las capabilities de los Ultra pero se etiqueta distinto en la UI.
  enterprise_plus: "Empresa",
};

interface UserProfile {
  id: string;
  name: string;
  email: string;
  id_company: string | null;
  company_name?: string | null;
  company_logo?: string | null;
  dev_tester?: boolean | null;
  grupo_testing?: string | null;
  plan?: string | null;
  role: string | null;
  max_fotos_mes?: number | null;
  creditos_restantes?: number | null;
  pais?: string | null;
  fecha_cancelacion_programada?: string | null;
  fecha_reactivacion_programada?: string | null;
  /** Motivo de la cancelación (`company_profiles.trigger_cancelacion`). */
  trigger_cancelacion?: string | null;
  /** Aviso de cobro rechazado; si hay, reemplaza a los de cancelación y pausa
   *  (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). */
  cobro?: EstadoDeCobro | null;
}

// Menú hamburguesa de mobile: oculto por ahora. La navegación principal en
// mobile pasó a la barra inferior (MobileBottomNav); ajustes / cambio de marca
// / cerrar sesión se acceden vía "Mi marca" → Configuración. Poner en `true`
// para volver a mostrarlo.
const SHOW_MOBILE_MENU_BUTTON = false;

/**
 * Un acceso del encabezado mobile que se puede plegar (WhatsApp, la campana) — ADR-2026-10-12-
 * el-encabezado-compacto-en-la-vista-de-plantilla. Siempre MONTADO: lo que cambia es sólo CSS
 * derivado de `oculto`, así no hay timers ni un desmontaje a mitad de transición que lo deje
 * a medio camino. Plegado, el ancho y el margen van a 0 (la pastilla de créditos, que está
 * antes con `ml-auto`, se desliza sola a la derecha) y el botón se desvanece achicándose.
 * `inert` lo saca del foco y de los lectores mientras no se ve.
 */
const PlegableDelEncabezado: React.FC<{ oculto: boolean; children: React.ReactNode }> = ({ oculto, children }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.toggleAttribute("inert", oculto);
  }, [oculto]);
  return (
    <div
      ref={ref}
      aria-hidden={oculto || undefined}
      className={`flex shrink-0 items-center transition-[max-width,margin,opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
        oculto ? "pointer-events-none ml-0 max-w-0 scale-90 opacity-0" : "ml-2 max-w-[48px] scale-100 opacity-100"
      }`}
    >
      {children}
    </div>
  );
};

export const Sidebar = () => {
  const location = useLocation();
  const {
    isCollapsed,
    toggleCollapsed,
    mobileBackOverride,
    mobileHideBack,
    mobileHeaderTransparent,
    mobileHeaderOscuro,
    mobileHeaderOculto,
    mobileHeaderCenter,
    mobileRightAction,
    mobileHeaderLeft,
    mobileHideCreditsPill,
    mobileHeaderCompacto,
    hideSidebar,
  } = useSidebar();

  // Animación de entrada/salida para `mobileHeaderCenter`: cuando el
  // contenido cambia entre null ↔ JSX, hacemos un fade + slide-up
  // sutil (200ms). Cambios in-place (mismo no-null → otro no-null,
  // ej. el toggle Imagen/Video cambiando su tab activo) se aplican
  // sin reanimar — `centerVisible` ya está true.
  const [displayedCenter, setDisplayedCenter] =
    useState<React.ReactNode | null>(mobileHeaderCenter);
  const [centerVisible, setCenterVisible] = useState<boolean>(
    !!mobileHeaderCenter,
  );
  useEffect(() => {
    if (mobileHeaderCenter) {
      setDisplayedCenter(mobileHeaderCenter);
      const id = requestAnimationFrame(() => setCenterVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setCenterVisible(false);
    const timer = setTimeout(() => setDisplayedCenter(null), 200);
    return () => clearTimeout(timer);
  }, [mobileHeaderCenter]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    try {
      const cached = localStorage.getItem("atlas-user-plan");
      if (cached) return { plan: cached } as UserProfile;
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ── Card flotante de ayuda al hover (solo desktop) ──
  // Compartido con la sidebar del shell nuevo — ver CardAyudaSidebar.tsx.
  const { abrirCardAyuda, cerrarCardAyuda, cardAyuda } = useCardAyudaSidebar();

  // Brand switcher state
  type BrandRow = {
    id: string;
    name: string;
    logo_url: string | null;
    is_default: boolean;
  };
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [switchingBrand, setSwitchingBrand] = useState(false);
  const [showMultiBrandRequiredModal, setShowMultiBrandRequiredModal] = useState(false);
  // Con facturación por marca sólo el dueño suma marcas (ADR-2026-10-28-marca-nueva-con-pago).
  const marcaNuevaSoloAdmin = useMarcaNuevaSoloAdmin();
  // Un plan pago que ya usó todas sus marcas: el tope que se muestra en el modal
  // (ADR-2026-10-27-planes-de-equipo-en-el-front). `undefined` = cerrado.
  const [topeDeMarcasAlcanzado, setTopeDeMarcasAlcanzado] = useState<number | null | undefined>(undefined);
  const lugarParaMarcas = useLugarParaMarcas(showBrandModal);

  const navigate = useNavigate();

  const activeBrand =
    brands.find((b) => b.id === currentBrandId) ??
    brands.find((b) => b.is_default) ??
    brands[0] ??
    null;

  // IMPORTANTE: si hay activeBrand, usar SOLO su logo. No caemos a userProfile.company_logo
  // porque eso muestra el logo de la marca default vieja en marcas adicionales sin logo.
  // Fallback a company_logo solo si no hay ninguna marca (legacy free tier sin onboarding).
  const displayLogo = activeBrand
    ? (activeBrand.logo_url || null)
    : (userProfile?.company_logo || null);
  const displayBrandName = activeBrand?.name || userProfile?.company_name || "";
  // OLD: mobileNavWidth and panelRef were for the slide-out panel — kept commented for reference
  // const [mobileNavWidth, setMobileNavWidth] = useState<number>(0);
  // const panelRef = useRef<HTMLDivElement | null>(null);


  // Get user permissions
  const { permissions } = usePermissions();

  // Siempre remover espaciado lateral de sidebar (ya no se usa en desktop)
  useEffect(() => {
    document.documentElement.classList.add("no-sidebar-spacing");
    document.body.classList.add("no-sidebar-spacing");
    return () => {
      // mantener la clase para todo el app
      document.documentElement.classList.add("no-sidebar-spacing");
      document.body.classList.add("no-sidebar-spacing");
    };
  }, []);

  // OLD: Compute dynamic mobile nav width — no longer needed with dropdown menu
  // useEffect(() => { ... setMobileNavWidth(width); ... }, []);

  // Sync internal state when other components dispatch the same toggle event
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.isMobileMenuOpen === "boolean") {
          setMobileMenuOpen(detail.isMobileMenuOpen);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener(
      "sidebar-mobile-toggle" as any,
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        "sidebar-mobile-toggle" as any,
        handler as EventListener,
      );
  }, []);

  // OLD: Apply/Remove inert on slide-out panel — no longer needed
  // useEffect(() => { panelRef.current... }, [mobileMenuOpen]);

  // Fetch user profile including company name
  // ¿La última lectura de saldo fue de una empresa que factura por marca? Sólo entonces un
  // cambio de marca cambia el número.
  const porMarcaRef = React.useRef(false);
  const fetchUserProfile = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await retrySupabase(
        async () =>
          await supabase
            .from("profiles")
            .select("id, name, email, id_company, role")
            .eq("id", user.id)
            .single(),
      );

      if (error) throw error;

      // If user has a company, fetch the company name and logo from company_profiles table
      if (profile.id_company) {
        const { data: companyData, error: companyError } = await retrySupabase(
          async () =>
            await supabase
              .from("company_profiles")
              .select(
                "nombre, logo_url, dev_tester, grupo_testing, plan, max_fotos_mes, pais, fecha_cancelacion_programada, fecha_reactivacion_programada, trigger_cancelacion, plataforma_pago, paid_plan_cancel_date",
              )
              .eq("id_company", profile.id_company)
              .single(),
        );

        if (!companyError && companyData) {
          // Saldo único (ADR-2026-10-27-facturacion-por-marca): el de la marca activa si la
          // empresa factura por marca. Si no se puede leer queda sin dato, como antes.
          const saldo = await leerSaldo(profile.id_company).catch(() => null);
          porMarcaRef.current = saldo?.facturaPorMarca === true;
          // Add the company name and logo to the profile object from company_profiles
          const profileWithCompanyInfo = {
            ...profile,
            company_name: companyData.nombre,
            company_logo: companyData.logo_url,
            dev_tester: companyData.dev_tester,
            grupo_testing: (companyData as any).grupo_testing,
            plan: companyData.plan,
            max_fotos_mes: saldo ? saldo.capacidad : companyData.max_fotos_mes,
            creditos_restantes: saldo ? saldo.disponibles : null,
            pais: companyData.pais,
            fecha_cancelacion_programada:
              companyData.fecha_cancelacion_programada,
            fecha_reactivacion_programada:
              (companyData as any).fecha_reactivacion_programada,
            trigger_cancelacion: (companyData as any).trigger_cancelacion,
            // Con facturación por marca, el de las marcas (ADR-2026-10-29-cobro-rechazado-por-marca).
            cobro: conSimulacion(await conCobroDeMarcas(estadoDeCobro({
              id_company: profile.id_company,
              plan: companyData.plan,
              plataforma_pago: (companyData as any).plataforma_pago ?? null,
              trigger_cancelacion: (companyData as any).trigger_cancelacion ?? null,
              fecha_cancelacion_programada: companyData.fecha_cancelacion_programada ?? null,
              paid_plan_cancel_date: (companyData as any).paid_plan_cancel_date ?? null,
              // Consulta aparte y tolerante: sólo en planes pagos (el rechazo en curso no
              // existe en gratis).
              ...(companyData.plan && companyData.plan !== "gratis"
                ? await leerRechazoDeCobro(profile.id_company)
                : {}),
            }), profile.id_company, saldo?.facturaPorMarca === true)),
          };
          setUserProfile(profileWithCompanyInfo);
          // Analytics: agrupamos al usuario bajo su empresa. Va acá y no en
          // App.tsx porque es el único lugar donde ya tenemos company_profiles
          // cargado — hacerlo en otro lado sería repetir la query.
          identifyEmpresa(profile.id_company, {
            name: companyData.nombre,
            plan: companyData.plan,
            pais: companyData.pais,
            dev_tester: companyData.dev_tester === true,
            grupo_testing: (companyData as any).grupo_testing,
          });
          try {
            localStorage.setItem(
              "atlas-user-plan",
              companyData.plan || "gratis",
            );
          } catch {}
          return; // Exit early since we've already set the profile
        }
      }

      // If we get here, we either don't have a company or couldn't fetch company data
      setUserProfile(profile);
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Cargar marcas y marca activa cuando hay id_company
  const fetchBrands = useCallback(async () => {
    if (!userProfile?.id_company) return;
    try {
      const { data: brandsData } = await supabase
        .from("brand_profiles")
        .select("id, name, logo_url, is_default")
        .eq("id_company", userProfile.id_company)
        .eq("is_draft", false)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      const list = (brandsData as BrandRow[] | null) ?? [];
      setBrands(list);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profData } = await supabase
        .from("profiles")
        .select("current_brand_id")
        .eq("id", user.id)
        .single();
      const fromProfile = (profData as any)?.current_brand_id as string | null;
      const fallback =
        list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
      setCurrentBrandId(fromProfile ?? fallback);
    } catch {
      // Silencioso: si falla queda en modo legacy
    }
  }, [userProfile?.id_company]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Escuchar cambios de marca disparados desde otras pantallas
  useEffect(() => {
    const handler = () => {
      fetchBrands();
    };
    window.addEventListener("atlas:brand-changed", handler);
    return () => window.removeEventListener("atlas:brand-changed", handler);
  }, [fetchBrands]);

  const switchBrand = useCallback(
    async (brandId: string) => {
      if (brandId === currentBrandId || switchingBrand) {
        setShowBrandModal(false);
        return;
      }
      // Una pantalla con trabajo sin guardar (el editor de un post de Instagram) pregunta
      // antes: el menú se cierra para que la pregunta no quede debajo
      // (ADR-2026-10-08-cambiar-de-marca-pregunta-si-hay-cambios).
      setShowBrandModal(false);
      if (!(await confirmarAntesDeCambiarDeMarca())) return;
      setSwitchingBrand(true);
      const prev = currentBrandId;
      setCurrentBrandId(brandId); // optimistic
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user");
        const { error } = await supabase
          .from("profiles")
          .update({ current_brand_id: brandId })
          .eq("id", user.id);
        if (error) throw error;
        // Notificar al resto de la app
        window.dispatchEvent(
          new CustomEvent("atlas:brand-changed", {
            detail: { brandId },
          }),
        );
        setShowBrandModal(false);

      } catch {
        setCurrentBrandId(prev);
      } finally {
        setSwitchingBrand(false);
      }
    },
    [currentBrandId, switchingBrand, location.pathname, navigate],
  );

  // Listen for global credit-change events (e.g. after generating an image/video)
  // and silently refetch the remaining-images counter.
  // We refetch twice (immediate + delayed) to cover the case where Supabase
  // replica lag returns the stale value right after a generation decrement.
  useEffect(() => {
    const handler = () => {
      fetchUserProfile(true);
      // Retry after a short delay to catch replication lag on the DB read.
      setTimeout(() => {
        fetchUserProfile(true);
      }, 2000);
    };
    window.addEventListener("atlas:credits-updated", handler);
    // El saldo es el de la marca activa cuando la empresa factura por marca.
    const alCambiarMarca = () => {
      if (porMarcaRef.current) handler();
    };
    window.addEventListener("atlas:brand-changed", alCambiarMarca);
    return () => {
      window.removeEventListener("atlas:credits-updated", handler);
      window.removeEventListener("atlas:brand-changed", alCambiarMarca);
    };
  }, [fetchUserProfile]);

  // Se removió el manejo de secciones/categorías para aplanar la navegación

  // Toggle function removed; App.tsx controls mobile menu and dispatches the event.

  // Close mobile menu when a link is clicked
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    window.dispatchEvent(
      new CustomEvent("sidebar-mobile-toggle", {
        detail: { isMobileMenuOpen: false },
      }),
    );
  };

  // Mobile nav selection: close menu
  const handleMobileNavSelection = () => {
    closeMobileMenu();
  };

  // Also close mobile menu when the route changes
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      window.dispatchEvent(
        new CustomEvent("sidebar-mobile-toggle", {
          detail: { isMobileMenuOpen: false },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Items permitidos según permisos y dev_tester.
  const isDevTester = userProfile?.dev_tester === true;
  // Pro y Ultra: el mismo piso que `getModePlanLock('editor-video')`
  // (ADR-2026-10-11-el-estudio-de-video-es-de-pro-y-ultra).
  const planLlegaAlEstudio = isProOrUltra(userProfile?.plan);
  const permittedFlatItems = FLAT_MENU_ITEMS.filter((item) => {
    if (item.alwaysShow) return true;
    // El estudio (Interfaz V2 + Modos) lo ve todo el mundo, sin permiso/devOnly.
    if (item.studioFlow === "v2") return true;
    if (!permissions[item.permissionKey]) return false;
    if (!item.devOnly) return true;
    if (isDevTester) return true;
    // El Estudio de Video se desbloquea para los planes Pro y Ultra (negocio +
    // enterprise/enterprise1/2/3 + el custom Empresa enterprise_plus), aunque
    // siga gateado `devOnly` para el resto. Otros ítems devOnly (Test V1,
    // Manager de Redes IA) quedan ocultos — son herramientas internas reales.
    if (item.permissionKey === "editor-video" && planLlegaAlEstudio) return true;
    return false;
  }).map((item) => {
    // La Interfaz V2 se etiqueta "Estudio virtual"; para dev testers sigue
    // diciendo "Interfaz V2".
    if (item.studioFlow === "v2" && item.path === "/interfaz-v2" && !isDevTester) {
      return { ...item, name: "Estudio virtual" };
    }
    return item;
  });

  // Resaltado del ítem: lo que decida `NavLink` por su `to`, MÁS las
  // `rutasActivas` que el ítem declare. Se compara contra `location.pathname`
  // y no contra el `to`, así un ítem que es la puerta de una pantalla con
  // pestañas queda encendido en todas ellas.
  const estaResaltado = (item: FlatMenuItem, isActive: boolean) =>
    isActive ||
    (item.rutasActivas?.some(
      (ruta) =>
        location.pathname === ruta || location.pathname.startsWith(`${ruta}/`),
    ) ??
      false);

  // Toggle mobile menu locally (new dropdown style)
  const toggleMobileMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    window.dispatchEvent(
      new CustomEvent("sidebar-mobile-toggle", {
        detail: { isMobileMenuOpen: next },
      }),
    );
  };

  // El header mobile ya no muestra título; `hideBackAndTitle` solo decide si
  // se oculta el back. Se oculta en las "pantallas-tab" de la barra inferior (son
  // raíces de navegación: se cambian con las tabs, no con back). En `/interfaz-v2` se
  // respeta el back override del preset sheet (ahí sí debe haber back).
  const hideBackAndTitle =
    MOBILE_BOTTOM_NAV_PATHS.includes(location.pathname) && !mobileBackOverride;
  // All pages now use Apple HIG style for the mobile header

  return (
    <>
      {/* ══════ Mobile Header (expands to show menu) ══════ */}
      {/* Estilo V2 unificado en TODAS las pantallas: fondo transparente y
          botones redondos blancos flotantes (back + menú). El header ya no
          muestra título ni logo — los títulos, donde hagan falta, viven
          dentro de cada página (left-aligned, arriba de todo). */}
      {/* `hideSidebar` esconde la sidebar de escritorio Y este header: lo pide
          una pantalla que es una herramienta entera con su propio chrome, y en
          el teléfono esa pantalla necesita el viewport completo igual que en
          escritorio. Sin esto, los botones flotantes de back y menú quedaban
          encima del topbar del Estudio de Video
          (ADR-2026-09-16-el-estudio-de-video-en-el-telefono). */}
      {/* `pointer-events-none` en el header y `auto` en cada control: es una franja
          fija y transparente de 56px, y entera se comía los taps de lo que pasa por
          debajo — en las pantallas-tab (sin back ni menú) era una franja vacía que no
          dejaba usar el contenido que la página sube ahí
          (ADR-2026-10-05-mi-marca-cabecera-en-mobile). `pointer-events` se hereda, así
          que lo que la página pasa (`mobileHeaderLeft`, `mobileRightAction`) va envuelto. */}
      <header
        className={`pointer-events-none fixed left-0 right-0 md:hidden z-50 bg-transparent ${
          hideSidebar ? "hidden" : ""
        }`}
        style={{
          // Arranca DEBAJO de la barra de créditos cuando hay una —
          // ADR-2026-08-04-la-pastilla-de-creditos-va-abajo-del-cartel. La variable la
          // publica quien monta la barra midiéndola (`InterfazV2` en mobile); sin barra
          // vale 0 y esto es exactamente el `top-0` que había antes.
          // …y debajo del aviso de "generando de fondo" cuando lo hay, que se apila
          // bajo la barra de créditos y publica su alto igual que ella
          // (ADR-2026-09-06-el-aviso-de-generando-de-fondo).
          // …y `--aire-header-mobile`, que pone la pantalla que quiere el header más abajo
          // (hoy, el detalle de un lanzamiento: sin él, los botones quedaban a 4px del borde).
          // …y 8px fijos para TODAS las pantallas: los botones quedaban a 4px del borde de
          // arriba (ADR-2026-10-23-aire-arriba-de-los-botones-flotantes).
          top: "calc(8px + var(--barra-creditos-mobile, 0px) + var(--aviso-generando-mobile, 0px) + var(--aire-header-mobile, 0px))",
          paddingTop: "env(safe-area-inset-top, 0px)",
          // Con un visor encima, el header se va: si no, sus íconos se ven a través del
          // visor mientras entra (ADR-2026-10-28-el-header-se-va-con-el-visor).
          opacity: mobileHeaderOculto ? 0 : 1,
          visibility: mobileHeaderOculto ? "hidden" : "visible",
          transition: mobileHeaderOculto
            ? "opacity 120ms ease-out, visibility 0s linear 120ms"
            : "opacity 200ms ease-out",
        }}
      >
        {/* Chrome flotante: back (izq) | contenido centrado opcional | menú
            (der). Ya no hay título ni logo en el header — `hideBackAndTitle`
            ahora solo sirve para ocultar el back en pantallas de entrada
            (las pantallas-tab, como la home V2). */}
        <div className="relative flex items-center h-14 px-4">
          {!mobileHideBack && !hideBackAndTitle && (
            <button
              onClick={() =>
                mobileBackOverride
                  ? mobileBackOverride()
                  : window.history.back()
              }
              className={`pointer-events-auto inline-flex items-center justify-center shrink-0 active:opacity-50 transition-all w-12 h-12 rounded-full backdrop-blur-md ${
                // Sobre una pantalla oscura (el detalle de una creación), el vidrio oscuro
                // de la edición en el visor (ADR-2026-10-28-la-botonera-del-telefono-toma-el-estilo-de-editar).
                mobileHeaderOscuro
                  ? "bg-white/[0.08] border border-white/[0.08] text-white"
                  : "bg-white/95 shadow-md ring-1 ring-black/[0.04] text-brand-black/85"
              }`}
              aria-label="Volver"
            >
              <ChevronLeft size={26} />
            </button>
          )}
          {/* Lo que la página quiera pegado al botón de volver (el título de la
              pantalla del Agente). Trae sus propios márgenes. */}
          {mobileHeaderLeft && (
            <div className="pointer-events-auto contents">{mobileHeaderLeft}</div>
          )}
          {/* Contenido centrado opcional (ej. toggle Imagen/Video). NO es un
              título de página — eso vive dentro de cada página ahora. */}
          {displayedCenter && (
            <div
              className={`absolute left-1/2 -translate-x-1/2 transition-all duration-200 ease-out ${
                centerVisible
                  ? "pointer-events-auto opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
            >
              {displayedCenter}
            </div>
          )}
          {/* Pill de créditos / imágenes restantes — sólo en el header
              transparente (home de Interfaz V2 y pantallas del preset
              sheet). Lleva al user a Configuración / billing al tappear.
              Toma `ml-auto` para empujar al hamburger contra el borde
              derecho; cuando este pill no se renderiza, el hamburger
              toma `ml-auto`. */}
          {(() => {
            // El chrome flotante (créditos, WhatsApp, campana) se dibuja APENAS la pantalla
            // pide el header transparente, sin esperar el perfil: antes los tres esperaban
            // las tres consultas en fila (usuario → perfil → empresa) y aparecían tarde
            // (ADR-2026-10-12-el-chrome-del-telefono-no-espera-al-perfil). Sólo el NÚMERO
            // espera: mientras carga, la pastilla guarda su lugar con una rayita que late.
            const showCreditsPill = mobileHeaderTransparent && !mobileHideCreditsPill;
            const creditosRestantes =
              typeof userProfile?.creditos_restantes === "number"
                ? userProfile.creditos_restantes
                : null;
            const conPastilla = showCreditsPill && (loading || creditosRestantes !== null);
            return (
              <>
                {/* Sin pastilla (el perfil no cargó), WhatsApp y la campana quedaban pegados a la
                    izquierda: el `ml-auto` que los empuja a la derecha lo llevaba ella
                    (ADR-2026-10-23-el-encabezado-sin-creditos-sigue-a-la-derecha). */}
                {showCreditsPill && !conPastilla && <span aria-hidden="true" className="ml-auto" />}
                {/* Si el perfil no cargó (falló), la pastilla no se queda con la rayita. */}
                {conPastilla && (
                  <NavLink
                    to="/configuracion"
                    // 36px de alto, no 48 — ADR-2026-08-04-pastilla-de-creditos-mobile-mas-chica.
                    // La pastilla flota sobre la imagen del hero y a 48px competía con
                    // ella; es un DATO, no un control principal. El ícono baja a 16px y
                    // el número a 13, la misma escala que la pastilla de créditos del
                    // encabezado de desktop.
                    className={`pointer-events-auto ml-auto inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/85 text-[13px] font-semibold shrink-0`}
                    aria-label={
                      creditosRestantes === null
                        ? "Créditos restantes"
                        : `${creditosRestantes} créditos restantes`
                    }
                  >
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-4 h-4 object-contain"
                    />
                    {creditosRestantes === null ? (
                      <span
                        aria-hidden="true"
                        className={`block h-2.5 w-5 rounded-full bg-black/10 ${loading ? "animate-pulse" : ""}`}
                      />
                    ) : (
                      creditosRestantes
                    )}
                  </NavLink>
                )}
                {/* Acceso a soporte, pegado a la pastilla de créditos. Va sólo
                    donde va la pastilla y no en todo el header: son las pantallas
                    con chrome flotante (home de V2 y las del sheet de presets), y
                    sumarlo en el resto llenaría de botones cada pantalla.

                    Link DIRECTO a `wa.me` y no el modal del QR: acá siempre es un
                    teléfono (`md:hidden`), y mostrarle "escaneá este QR con tu
                    celular" a alguien que ya está en el celular no tiene sentido.

                    36px como la pastilla, no 48 como el back: es un acceso, no un
                    control principal — flota sobre la imagen del hero. */}
                {/* En la vista ampliada de una plantilla (`mobileHeaderCompacto`) WhatsApp y
                    la campana se pliegan: el selector Imagen/Video del centro no entraba con
                    los tres botones (ADR-2026-10-12-el-encabezado-compacto-en-la-vista-de-plantilla). */}
                {showCreditsPill && (
                  <PlegableDelEncabezado oculto={mobileHeaderCompacto}>
                    <a
                      href={urlsWhatsApp({ mensaje: MENSAJE_SOPORTE }).directa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`pointer-events-auto ${SHOW_MOBILE_MENU_BUTTON ? "mr-2" : ""} inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/95 text-brand-black/85 shadow-md ring-1 ring-black/[0.04] backdrop-blur-md active:opacity-70`}
                      aria-label="Escribinos por WhatsApp"
                    >
                      <Icon name="solid/whatsapp" size={18} />
                    </a>
                  </PlegableDelEncabezado>
                )}
                {/* Campana — ADR-2026-08-31-centro-de-notificaciones. Entra en el
                    chrome flotante, con la misma condición que la pastilla de
                    créditos y el acceso a WhatsApp (`showCreditsPill`): son las
                    pantallas con header transparente —la home de V2 y las del sheet
                    de presets—, y sumarla en el resto llenaría de botones cada
                    pantalla.

                    NO va como tab de la barra inferior: esa barra tiene 3-4
                    destinos de trabajo y un quinto de "avisos" le sacaría lugar a
                    algo que se usa mucho más. Acá el tap lleva derecho a
                    `/notificaciones` — un popover de 380px no entra en un teléfono. */}
                {showCreditsPill && (
                  <PlegableDelEncabezado oculto={mobileHeaderCompacto}>
                    <CampanaNotificaciones variante="mobile" className="pointer-events-auto" />
                  </PlegableDelEncabezado>
                )}
                {SHOW_MOBILE_MENU_BUTTON && (
                  <button
                    onClick={toggleMobileMenu}
                    className={`pointer-events-auto ${showCreditsPill ? "" : "ml-auto"} inline-flex items-center justify-center transition-all shrink-0 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/70`}
                    aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
                  >
                    <Menu
                      size={24}
                      className={`transition-transform duration-300 ${mobileMenuOpen ? "rotate-90 opacity-0 absolute" : "rotate-0 opacity-100"}`}
                    />
                    <X
                      size={24}
                      className={`transition-transform duration-300 ${mobileMenuOpen ? "rotate-0 opacity-100" : "-rotate-90 opacity-0 absolute"}`}
                    />
                  </button>
                )}
              </>
            );
          })()}
          {/* Acción a la derecha inyectada por la página (ej. botón "Listo"
              de la grilla de resultados). Trae su propio `ml-auto`. */}
          {/* Lo que la página pone a la derecha ("Listo", "Guardar"): tocable, como
              `mobileHeaderLeft` (el header es `pointer-events-none`). */}
          {mobileRightAction && (
            <div className="pointer-events-auto contents">{mobileRightAction}</div>
          )}
        </div>
      </header>

      {/* ══════ Floating popover menu ══════ */}
      <div
        className={`fixed right-4 w-[272px] md:hidden z-[51] origin-top-right transition-all duration-200 ease-out ${
          mobileMenuOpen
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-[0.9] pointer-events-none"
        }`}
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}
      >
        <div className="bg-white rounded-[14px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] overflow-hidden">
          {/* ── Top section: credits bar, upgrade CTA, workspace card ── */}
          {!loading && userProfile && (
            <div className="px-3 pt-3 pb-2.5 flex flex-col gap-2.5">
              {/* Credits bar */}
              {(() => {
                const remaining =
                  typeof userProfile.creditos_restantes === "number"
                    ? userProfile.creditos_restantes
                    : 0;
                const max =
                  typeof userProfile.max_fotos_mes === "number"
                    ? userProfile.max_fotos_mes
                    : 0;
                const isFree = userProfile.plan === "gratis";
                const pct = isFree
                  ? max > 0
                    ? (remaining / max) * 100
                    : 0
                  : max > 0
                    ? (Math.min(remaining, max) / max) * 100
                    : 0;
                return (
                  <NavLink
                    to="/configuracion"
                    onClick={handleMobileNavSelection}
                    className="block px-1 py-0.5"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] text-brand-black/80">
                        {/* "créditos" para todos los planes (ADR-2026-07-31-creditos-toda-app-configuracion-plan), igual que
                            el contador de desktop. */}
                        {remaining} créditos restantes
                      </span>
                    </div>
                    <div className="h-[5px] bg-[#F2F2F7] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-brand-primary transition-all duration-300"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </NavLink>
                );
              })()}

              {/* Upgrade CTA (free plan only) */}
              {userProfile.plan === "gratis" && (
                <div className="flex items-center gap-2.5 bg-brand-primary rounded-[9px] px-3 h-[44px]">
                  <span className="material-symbols-rounded text-white text-[17px] shrink-0">
                    auto_awesome
                  </span>
                  <span className="text-[13px] text-white flex-1">
                    Mejorá tu plan
                  </span>
                  <NavLink
                    to="/subscription"
                    onClick={handleMobileNavSelection}
                    className="shrink-0 text-[13px] font-semibold text-brand-primary bg-white px-2.5 py-1 rounded-[6px] active:opacity-80 transition-opacity"
                  >
                    Ver planes
                  </NavLink>
                </div>
              )}

              {/* Workspace card — click abre modal de cambio de marca si hay >1 marca */}
              <div
                className="flex items-center gap-2.5 bg-[#F2F2F7] rounded-[9px] px-2.5 h-[44px] cursor-pointer active:opacity-80 transition-opacity"
                onClick={() => {
                  if (brands.length > 1) {
                    setShowBrandModal(true);
                  } else {
                    handleMobileNavSelection();
                    navigate("/configuracion");
                  }
                }}
              >
                <img
                  src={
                    displayLogo
                      ? (displayLogo.includes("supabase.co")
                          ? `${displayLogo}?width=32&height=32`
                          : displayLogo)
                      : logoDeBranding('solo')
                  }
                  alt="Logo"
                  className="w-8 h-8 rounded-[7px] object-contain bg-white shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      logoDeBranding('solo');
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-brand-black/90 truncate">
                    {userProfile.name || "Usuario"}
                  </div>
                  {displayBrandName && (
                    <div className="text-[11px] text-[#3C3C43]/60 truncate">
                      {displayBrandName}
                    </div>
                  )}
                </div>
                {brands.length > 1 ? (
                  <ChevronsUpDown className="w-4 h-4 text-neutral-400 shrink-0" />
                ) : userProfile.plan ? (
                  <span className="shrink-0 text-[11px] font-medium text-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.08)] px-1.5 py-0.5 rounded-full">
                    {PLAN_LABELS[userProfile.plan] || nombreDelPlanDeEquipo(userProfile.plan) || userProfile.plan}
                  </span>
                ) : null}
              </div>

              {/* Cobro rechazado: reemplaza a los avisos de cancelación y pausa */}
              {userProfile.cobro && (
                <AvisoDeCobro
                  estado={userProfile.cobro}
                  variante="compacto"
                  onNavegar={handleMobileNavSelection}
                />
              )}

              {/* Scheduled cancellation notice */}
              {!userProfile.cobro && userProfile.fecha_cancelacion_programada && (
                <div
                  className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-[9px] px-3 py-2.5 cursor-pointer active:opacity-80 transition-opacity"
                  onClick={() => {
                    handleMobileNavSelection();
                    navigate("/subscription");
                  }}
                >
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[13px] font-medium text-amber-800">
                      Cancelación programada
                    </p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Tu plan se cancelará el{" "}
                      {new Date(
                        userProfile.fecha_cancelacion_programada,
                      ).toLocaleDateString("es-AR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Paused subscription notice */}
              {!userProfile.cobro &&
                !userProfile.fecha_cancelacion_programada &&
                userProfile.fecha_reactivacion_programada &&
                new Date(userProfile.fecha_reactivacion_programada) >
                  new Date() && (
                  <div
                    className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-[9px] px-3 py-2.5 cursor-pointer active:opacity-80 transition-opacity"
                    onClick={() => {
                      handleMobileNavSelection();
                      navigate("/subscription");
                    }}
                  >
                    <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[13px] font-medium text-blue-800">
                        Suscripción pausada
                      </p>
                      <p className="text-[11px] text-blue-700 mt-0.5">
                        Se reanudará el{" "}
                        {new Date(
                          userProfile.fecha_reactivacion_programada,
                        ).toLocaleDateString("es-AR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* ── Main nav items ── */}
          <div className="px-1.5 pb-0.5">
            {permittedFlatItems.map((item: FlatMenuItem) => (
              <React.Fragment key={item.name}>
                {item.abreCategoria && (
                  <p className="px-2.5 pt-3 pb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-[rgb(var(--brand-black-rgb)_/_0.35)]">
                    {item.category}
                  </p>
                )}
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                      estaResaltado(item, isActive)
                        ? "bg-[rgb(var(--brand-primary-rgb)_/_0.08)] text-brand-primary"
                        : "text-brand-black/85 active:bg-black/[0.04]"
                    }`
                  }
                  onClick={handleMobileNavSelection}
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                          estaResaltado(item, isActive)
                            ? "bg-brand-primary text-white"
                            : "bg-[#F2F2F7] text-[#3C3C43]/70"
                        }`}
                      >
                        {item.icon}
                      </span>
                      <span>{item.name}</span>
                    </>
                  )}
                </NavLink>
              </React.Fragment>
            ))}
          </div>

          {/* ── Separator ── */}
          <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

          {/* ── Bottom section ── */}
          <div className="px-1.5 py-0.5">
            {/* "Invitá y ganá" (ADR-2026-08-27-programa-referidos). Va en el pie
                y no entre las herramientas de generación: no es una forma de
                hacer contenido, es una sección de la cuenta —como Ayuda y
                Configuración—. Pero PRIMERA de las tres: es la única que le
                ofrece algo al usuario en vez de pedirle algo. */}
            <NavLink
              to="/referidos"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive
                    ? "bg-[rgb(var(--brand-primary-rgb)_/_0.08)] text-brand-primary"
                    : "text-brand-black/85 active:bg-black/[0.04]"
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                      isActive
                        ? "bg-brand-primary text-white"
                        : "bg-[#F2F2F7] text-[#3C3C43]/70"
                    }`}
                  >
                    <span className="material-symbols-rounded">redeem</span>
                  </span>
                  <span>Invitá y ganá</span>
                </>
              )}
            </NavLink>
            <NavLink
              to="/ayuda"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive
                    ? "bg-[rgb(var(--brand-primary-rgb)_/_0.08)] text-brand-primary"
                    : "text-brand-black/85 active:bg-black/[0.04]"
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                      isActive
                        ? "bg-brand-primary text-white"
                        : "bg-[#F2F2F7] text-[#3C3C43]/70"
                    }`}
                  >
                    <span className="material-symbols-rounded">help</span>
                  </span>
                  <span>Ayuda</span>
                </>
              )}
            </NavLink>
            <NavLink
              to="/configuracion"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive
                    ? "bg-[rgb(var(--brand-primary-rgb)_/_0.08)] text-brand-primary"
                    : "text-brand-black/85 active:bg-black/[0.04]"
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                      isActive
                        ? "bg-brand-primary text-white"
                        : "bg-[#F2F2F7] text-[#3C3C43]/70"
                    }`}
                  >
                    <span className="material-symbols-rounded">settings</span>
                  </span>
                  <span>Configuración</span>
                </>
              )}
            </NavLink>
          </div>

          {/* ── Separator ── */}
          <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

          {/* ── Sign out ── */}
          <div className="px-1.5 py-0.5 pb-1.5">
            <button
              type="button"
              onClick={async () => {
                closeMobileMenu();
                await cerrarSesion();
              }}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-brand-black/85 active:bg-black/[0.04] transition-colors w-full"
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] bg-[#F2F2F7] text-[#3C3C43]/70">
                <span className="material-symbols-rounded">logout</span>
              </span>
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </div>

      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 md:hidden z-40 transition-opacity duration-300 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* ══════ Desktop Sidebar ══════ */}
      {/* `hideSidebar`: una pantalla que es una herramienta entera y trae su
          propia salida (el Estudio de Video) la esconde mientras está montada.
          Va por `display:none` y no por el atributo `hidden`, que en este
          elemento no haría nada: `md:flex` es una regla de autor y le gana al
          display del user-agent. Escondida no se ve ni toma foco de teclado.
          El header MOBILE sigue: en pantallas chicas es la única navegación
          que hay. */}
      <aside
        className={`${hideSidebar ? "hidden" : "hidden md:flex"} fixed z-30 flex-col bg-[#F3F3F3] transition-[width] duration-300 ease-out`}
        style={{ width: isCollapsed ? 72 : 264, top: 0, left: 0, bottom: 0 }}
      >
        {/* ── Logo + toggle de contraer (icono right-aligned a la altura del
            título; colapsado = el logo expande al tocarlo) ── */}
        <div
          className={`flex items-center h-16 shrink-0 ${isCollapsed ? "justify-center px-0" : "pl-6 pr-4"}`}
        >
          {isCollapsed ? (
            // Colapsado: en vez del logo, mostramos el botón para expandir
            // (espejo del ChevronsLeft de contraer), así queda claro que es
            // la acción de des-minimizar la sidebar.
            <button
              onClick={toggleCollapsed}
              title="Expandir"
              aria-label="Expandir menú"
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#2B2B2B]/55 hover:text-[#2B2B2B]/90 hover:bg-black/[0.04] transition-colors shrink-0"
            >
              <ChevronsRight size={18} />
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-sm bg-brand-primary flex items-center justify-center shrink-0">
                  <LogoDeMarca
                    variante="soloBlanco"
                    alt=""
                    className="w-4 h-4"
                  />
                </div>
                <span className="text-[19px] leading-none text-[#2B2B2B] whitespace-nowrap">
                  <span className="font-display italic font-semibold">Estudio</span>
                  <span className="font-sans font-bold ml-0.5">Atlas</span>
                </span>
              </div>
              <button
                onClick={toggleCollapsed}
                title="Contraer"
                className="ml-auto -mr-3.5 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#2B2B2B]/55 hover:text-[#2B2B2B]/90 hover:bg-black/[0.04] transition-colors"
              >
                <ChevronsLeft size={18} />
              </button>
            </>
          )}
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 flex flex-col gap-0.5 px-4 pt-2 pb-3 overflow-y-auto">
          {permittedFlatItems.map((item: FlatMenuItem) => (
            <React.Fragment key={item.name}>
              {item.abreCategoria && !isCollapsed && (
                <p className="px-2.5 pt-3 pb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-[rgb(var(--brand-black-rgb)_/_0.35)]">
                  {item.category}
                </p>
              )}
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                    estaResaltado(item, isActive)
                      ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                      : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
                  }`
                }
                // Sin title nativo cuando hay card propio: mostraría el
                // tooltip del browser encima del nuestro.
                title={item.descripcion ? undefined : item.name}
                onMouseEnter={
                  item.descripcion
                    ? (e) =>
                        abrirCardAyuda(
                          e.currentTarget,
                          item.name,
                          item.descripcion!,
                        )
                    : undefined
                }
                onMouseLeave={item.descripcion ? cerrarCardAyuda : undefined}
                onClick={cerrarCardAyuda}
              >
                <span className="shrink-0 inline-flex items-center [&>.material-symbols-rounded]:text-[18px]">
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <span className="text-[13px] whitespace-nowrap overflow-hidden">
                    {item.name}
                  </span>
                )}
              </NavLink>
            </React.Fragment>
          ))}

          {/* Divider */}
          <div
            className={`my-1.5 h-px bg-black/[0.07] ${isCollapsed ? "mx-1.5" : "mx-1"}`}
          />

          <NavLink
            to="/referidos"
            className={({ isActive }) =>
              `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                isActive
                  ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                  : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
              }`
            }
            title="Invitá y ganá"
          >
            <span className="material-symbols-rounded text-[18px] shrink-0">
              redeem
            </span>
            {!isCollapsed && (
              <span className="text-[13px] whitespace-nowrap overflow-hidden">
                Invitá y ganá
              </span>
            )}
          </NavLink>
          <NavLink
            to="/ayuda"
            className={({ isActive }) =>
              `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                isActive
                  ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                  : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
              }`
            }
            title="Ayuda"
          >
            <span className="material-symbols-rounded text-[18px] shrink-0">
              help
            </span>
            {!isCollapsed && (
              <span className="text-[13px] whitespace-nowrap overflow-hidden">
                Ayuda
              </span>
            )}
          </NavLink>
          <NavLink
            to="/configuracion"
            className={({ isActive }) =>
              `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                isActive
                  ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                  : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
              }`
            }
            title="Configuración"
          >
            <span className="material-symbols-rounded text-[18px] shrink-0">
              settings
            </span>
            {!isCollapsed && (
              <span className="text-[13px] whitespace-nowrap overflow-hidden">
                Configuración
              </span>
            )}
          </NavLink>
        </nav>

        {/* ── User card at bottom ──
            Acá iba el cartel azul "Te regalamos 30 imágenes gratis más / Reclamar"
            para el plan gratis. Se sacó (ADR-2026-07-31-fuera-cartel-30-imagenes-gratis): la oferta ya la comunica la barra
            superior de créditos, que además habla del descuento real del cupón
            vigente (ADR-2026-07-30-titulos-tira-modos-entran-linea) en vez de prometer un regalo fijo. La barra de fotos
            restantes de acá abajo se queda: es estado, no promoción. */}
        <div className="shrink-0 px-4 pb-3">
          {/* ── Créditos restantes (arriba del container del perfil) ── */}
          {!loading &&
            userProfile &&
            !isCollapsed &&
            (() => {
              // Este contador y el aviso de créditos de arriba salen del mismo dato
              // (ADR-2026-07-31-contador-sidebar-habla-creditos-sale) y se mantienen
              // sincronizados por el evento `atlas:credits-updated`.
              const remaining =
                typeof userProfile.creditos_restantes === "number"
                  ? userProfile.creditos_restantes
                  : 0;
              const plan = userProfile.plan;
              const max =
                typeof userProfile.max_fotos_mes === "number"
                  ? userProfile.max_fotos_mes
                  : 0;
              const isFree = plan === "gratis";
              const pct = isFree
                ? max > 0
                  ? (remaining / max) * 100
                  : 0
                : max > 0
                  ? (Math.min(remaining, max) / max) * 100
                  : 0;
              return (
                <div
                  className="mb-3 px-1 cursor-pointer"
                  onClick={() => navigate("/configuracion")}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    {/* "créditos" para todos los planes (ADR-2026-07-31-contador-sidebar-habla-creditos-sale). Antes decía
                        "fotos gratuitas", "imágenes" o "créditos" según el plan:
                        tres nombres para la misma unidad, y la barra de arriba ya
                        la llamaba créditos. La lógica del número y del porcentaje
                        no cambia. */}
                    <span className="text-[11px] text-brand-black/55">
                      {remaining} créditos restantes
                    </span>
                  </div>
                  <div className="h-[4px] bg-black/[0.08] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-primary transition-all duration-300"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })()}

          {loading ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-5 h-5 animate-spin text-brand-black/30" />
            </div>
          ) : userProfile ? (
            <div
              className={`cursor-pointer transition-colors ${isCollapsed ? "p-2 flex justify-center" : "px-1 py-2 flex items-center gap-3 rounded-md hover:bg-black/[0.03]"}`}
              onClick={() => {
                if (brands.length > 1) {
                  setShowBrandModal(true);
                } else {
                  navigate("/configuracion");
                }
              }}
            >
              <img
                src={
                  displayLogo
                    ? (displayLogo.includes("supabase.co")
                        ? `${displayLogo}?width=40&height=40`
                        : displayLogo)
                    : logoDeBranding('solo')
                }
                alt="Logo"
                className="w-8 h-8 rounded-full object-contain bg-white shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    logoDeBranding('solo');
                }}
              />
              {!isCollapsed && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="text-[length:var(--text-body)] font-medium text-brand-black truncate">
                      {userProfile.name || "Usuario"}
                    </div>
                    {displayBrandName && (
                      <div className="text-[length:var(--text-caption)] text-[#2B2B2B]/60 truncate">
                        {displayBrandName}
                      </div>
                    )}
                  </div>
                  {brands.length > 1 ? (
                    <ChevronsUpDown className="w-4 h-4 text-brand-black/45 shrink-0" />
                  ) : userProfile.plan ? (
                    <span className="shrink-0 text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded bg-black/[0.06] text-brand-black/65">
                      {PLAN_LABELS[userProfile.plan] || nombreDelPlanDeEquipo(userProfile.plan) || userProfile.plan}
                    </span>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {/* ── Cobro rechazado (desktop legacy) ── */}
          {!loading && userProfile?.cobro && !isCollapsed && (
            <AvisoDeCobro estado={userProfile.cobro} variante="compacto" className="mt-2" />
          )}

          {/* ── Scheduled cancellation notice (desktop) ── */}
          {!loading &&
            !userProfile?.cobro &&
            userProfile?.fecha_cancelacion_programada &&
            !isCollapsed && (
              <div
                className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate("/subscription")}
              >
                <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-medium text-amber-700">
                    Cancelación programada
                  </p>
                  <p className="text-[11px] text-amber-600/80 mt-0.5">
                    Tu plan se cancelará el{" "}
                    {new Date(
                      userProfile.fecha_cancelacion_programada,
                    ).toLocaleDateString("es-AR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            )}

          {/* ── Paused subscription notice (desktop) ── */}
          {!loading &&
            userProfile &&
            !userProfile.cobro &&
            !userProfile.fecha_cancelacion_programada &&
            userProfile.fecha_reactivacion_programada &&
            new Date(userProfile.fecha_reactivacion_programada) >
              new Date() &&
            !isCollapsed && (
              <div
                className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate("/subscription")}
              >
                <AlertCircle className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-medium text-blue-700">
                    Suscripción pausada
                  </p>
                  <p className="text-[11px] text-blue-600/80 mt-0.5">
                    Se reanudará el{" "}
                    {new Date(
                      userProfile.fecha_reactivacion_programada,
                    ).toLocaleDateString("es-AR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            )}
        </div>
      </aside>

      {/* ══════ Card flotante de ayuda de los ítems del sidebar (desktop) ══════ */}
      {cardAyuda}

      {/* ══════ Brand switcher modal ══════ */}
      {showBrandModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => !switchingBrand && setShowBrandModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Cambiar de marca
              </h3>
              <button
                onClick={() => setShowBrandModal(false)}
                disabled={switchingBrand}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto scrollbar-thin">
              {brands.map((b) => {
                const isActive = b.id === currentBrandId;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => switchBrand(b.id)}
                    disabled={switchingBrand}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-neutral-50 transition-colors disabled:opacity-60"
                  >
                    <img
                      src={
                        b.logo_url
                          ? (b.logo_url.includes("supabase.co")
                              ? `${b.logo_url}?width=40&height=40`
                              : b.logo_url)
                          : logoDeBranding('solo')
                      }
                      alt=""
                      className="w-9 h-9 rounded-[7px] object-contain bg-white border border-neutral-200 shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          logoDeBranding('solo');
                      }}
                    />
                    <span className="flex-1 text-[14px] font-medium text-brand-black/90 truncate">
                      {b.name}
                    </span>
                    {isActive && (
                      <Check className="w-4 h-4 text-brand-primary shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-neutral-200">
              <button
                type="button"
                disabled={marcaNuevaSoloAdmin}
                onClick={async () => {
                  const decision = await decidirMarcaNueva();
                  if (decision === "solo_admin") return;
                  // Con facturación por marca no hay tope: cada marca paga su plan
                  // (ADR-2026-10-28-facturacion-por-marca-sin-tope-de-marcas).
                  const lugar = await leerLugarParaMarcas();
                  if (!lugar.puedeSumar) {
                    setShowBrandModal(false);
                    // Gratis sigue con su cartel de siempre; un plan pago, con el de su tope.
                    if (isPaidPlan(lugar.plan)) setTopeDeMarcasAlcanzado(lugar.tope);
                    else setShowMultiBrandRequiredModal(true);
                    return;
                  }
                  setShowBrandModal(false);
                  navigate(
                    "/redes-sociales/descripcion-brand?mode=additional",
                  );
                }}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-neutral-50 transition-colors text-brand-primary disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <div className="w-9 h-9 rounded-[7px] border border-dashed border-brand-primary/40 flex items-center justify-center shrink-0">
                  {lugarParaMarcas && !lugarParaMarcas.puedeSumar ? (
                    <Lock className="w-4 h-4" aria-label="Sin lugar en tu plan" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </div>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-medium">
                    Añadir nueva marca
                  </span>
                  {marcaNuevaSoloAdmin ? (
                    <span className="block text-[12px] leading-[16px] text-black/50">
                      {MOTIVO_SOLO_ADMIN}
                    </span>
                  ) : textoDeMarcasUsadas(lugarParaMarcas) && (
                    <span className="block text-[12px] text-neutral-500 truncate">
                      {textoDeMarcasUsadas(lugarParaMarcas)}
                    </span>
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showMultiBrandRequiredModal}
        onClose={() => setShowMultiBrandRequiredModal(false)}
        type="multi-brand-required"
      />
      <SubscriptionLimitModal
        isOpen={topeDeMarcasAlcanzado !== undefined}
        onClose={() => setTopeDeMarcasAlcanzado(undefined)}
        type="tope-de-marcas"
        tope={topeDeMarcasAlcanzado}
      />
    </>
  );
};
